<template>
  <transition name="fade">
    <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
      <div class="relative w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <button @click="closeModal" class="absolute right-4 top-4 inline-flex items-center justify-center rounded-full p-2 text-slate-500 hover:bg-slate-100">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div class="grid gap-6 p-6 md:p-10">
          <div>
            <h3 class="text-lg font-semibold text-slate-900">Adjust your photo</h3>
            <p class="text-sm text-slate-500">Drag the image to center yourself and use the zoom control to get the framing just right.</p>
          </div>

          <div class="flex flex-col gap-6 md:flex-row">
            <div class="relative mx-auto w-full max-w-lg overflow-hidden rounded-3xl border border-slate-100 bg-slate-50">
              <div class="aspect-square w-full">
                <img
                  ref="image"
                  :src="image"
                  class="h-full w-full object-contain"
                  alt="Uploaded image"
                  @load="handleImageLoad"
                />
              </div>
            </div>

      <div class="flex w-full max-w-sm flex-col gap-5">
        <div class="rounded-3xl border border-slate-100 bg-slate-50 p-5">
          <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">Live preview</p>
          <div class="mt-4 flex items-center justify-center">
            <div class="h-32 w-32 overflow-hidden rounded-full border border-slate-200 bg-white">
              <img v-if="previewDataUrl" :src="previewDataUrl" alt="Crop preview" class="h-full w-full object-cover" />
            </div>
          </div>
        </div>

        <div>
          <p class="text-xs text-slate-500">Drag the crop handles to fine-tune your framing.</p>
        </div>
            </div>
          </div>

          <div class="flex flex-col gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:justify-end">
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
              @click="closeModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand/20 transition hover:bg-brand/90"
              @click="save"
            >
              Save photo
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

export default {
  props: {
    image: String,
    show: Boolean
  },
  data() {
    return {
      cropper: null,
      previewDataUrl: null,
      currentZoomRatio: 1
    };
  },
  computed: {
    zoomDisplay() {
      return this.currentZoomRatio.toFixed(1);
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.previewDataUrl = this.image || null;
        this.$nextTick(() => {
          if (this.$refs.image && this.$refs.image.complete) {
            this.handleImageLoad();
          }
        });
      } else {
        this.destroyCropper();
      }
    },
    image() {
      if (this.show) {
        this.$nextTick(() => {
          if (this.$refs.image && this.$refs.image.complete) {
            this.handleImageLoad();
          }
        });
      } else {
        this.previewDataUrl = this.image || null;
      }
    }
  },
  beforeUnmount() {
    this.destroyCropper();
  },
  methods: {
    initCropper() {
      if (!this.$refs.image) {
        return;
      }
      this.destroyCropper();
      this.cropper = new Cropper(this.$refs.image, {
        aspectRatio: 1,
        viewMode: 1,
        responsive: true,
        background: false,
        guides: false,
        center: true,
        autoCropArea: 0.9,
        dragMode: 'move',
        zoomOnWheel: false,
        cropBoxResizable: true,
        minCropBoxWidth: 120,
        minCropBoxHeight: 120,
        ready: () => {
          this.currentZoomRatio = 1;
          this.updatePreview();
        },
        crop: () => this.updatePreview(),
      });
    },
    destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    handleImageLoad() {
      if (!this.$refs.image || !this.$refs.image.complete) {
        return;
      }
      this.initCropper();
    },
    updatePreview() {
      if (!this.cropper) {
        this.previewDataUrl = this.image || null;
        return;
      }
      try {
        const canvas = this.cropper.getCroppedCanvas({ width: 320, height: 320 });
        if (canvas) {
          this.previewDataUrl = canvas.toDataURL('image/webp', 0.9);
          return;
        }
      } catch (error) {
        // ignore and fallback
      }
      this.previewDataUrl = this.image || null;
    },
    save() {
      if (!this.cropper) {
        return;
      }
      const croppedImage = this.cropper.getCroppedCanvas({ width: 800, height: 800 }).toDataURL('image/webp', 0.92);
      this.$emit('save', croppedImage);
      this.closeModal();
    },
    closeModal() {
      this.$emit('close');
    }
  }
};
</script>
  
